import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ListChecks } from 'lucide-react'
import { mutate } from '@/runtime'
import { useListsFor } from '@/db/hooks'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import { WS } from '@/db/ops'
import { Icon } from '@/lib/icons'
import { Button, Field, Sheet, cx } from '@/components/ui'
import type { Resource } from '@/db/types'
import type { ListResource } from './apply'
import { LIST_STYLES, type ListSpec, type ListStatus } from './schema'

type Props = { resource: Resource<ListSpec, ListStatus> }

/** Open items as a short preview: "Milk, Bread and more" (named, never a count of what is left). */
const preview = (items: ListSpec['items']) => {
  const open = items.filter((i) => !i.done)
  const shown = open.slice(0, 3).map((i) => i.text)
  const rest = open.length - shown.length
  return shown.join(', ') + (rest > 0 ? ' and more' : '')
}

/** A list in a listing: icon, title, and what is still open. No counts of undone things. */
export const ListRow = ({ resource: l }: Props) => (
  <li data-list-id={l.id}>
    <Link
      to="/lists/$id"
      params={{ id: l.id }}
      className="flex items-center gap-3 px-3.5 py-3 hover:bg-raised/50"
    >
      <span className="shrink-0 text-lg leading-none">
        <Icon value={l.spec.icon} size={18} fallback={<ListChecks size={18} className="text-muted" />} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-fg">{l.spec.title}</span>
        <span className="block truncate text-xs text-faint">
          {l.spec.items.length === 0
            ? 'Empty'
            : l.spec.items.every((i) => i.done)
              ? 'All ticked'
              : preview(l.spec.items)}
        </span>
      </span>
      {l.spec.pinned && <span className="text-xs text-faint">pinned</span>}
    </Link>
  </li>
)

/** Read-only projection for generic surfaces; the real thing is the list route. */
export const ListDetail = ({ resource }: Props) => (
  <ul className="text-sm leading-6">
    {resource.spec.items.map((i) => (
      <li key={i.id} className={cx(i.done && 'line-through text-faint')}>
        {i.text}
      </li>
    ))}
  </ul>
)

/** Create a list in the given workspace, optionally under a project, task or page. */
export const createList = async (ws: string | undefined, parentId?: string, spec: Partial<ListSpec> = {}) => {
  const labels: Record<string, string> = ws && ws !== ALL ? { [WS]: ws } : {}
  const r = (await mutate.create(
    'List',
    { title: 'New list', style: 'checklist', items: [], pinned: false, ...spec },
    labels,
  )) as ListResource
  if (parentId) await mutate.setEdge(r.id, parentId, 'belongs_to')
  return r
}

const formSchema = z.object({
  title: z.string().min(1, 'give it a name').max(200),
  style: z.enum(LIST_STYLES),
  pinned: z.boolean(),
})
type FormValues = z.output<typeof formSchema>

/** Name, style and pin. Without a resource it creates a list in the current scope. */
export const ListForm = ({
  resource,
  parentId,
  ws,
  onDone,
}: {
  resource?: ListResource
  parentId?: string
  /** workspace for a new list; defaults to the current scope */
  ws?: string
  onDone: () => void
}) => {
  const [error, setError] = useState<string | null>(null)
  const scopeWs = useScope((s) => s.ws)
  const scope = ws ?? scopeWs
  const navigate = useNavigate()
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: resource
      ? { title: resource.spec.title, style: resource.spec.style, pinned: resource.spec.pinned }
      : { title: '', style: 'checklist', pinned: false },
  })
  const submit = async (v: FormValues) => {
    setError(null)
    try {
      if (resource) {
        await mutate.patch(resource.id, { spec: { title: v.title.trim(), style: v.style, pinned: v.pinned } })
      } else {
        const r = await createList(scope, parentId, {
          title: v.title.trim(),
          style: v.style,
          pinned: v.pinned,
        })
        void navigate({ to: '/lists/$id', params: { id: r.id } })
      }
      onDone()
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <Sheet open onClose={onDone} title={resource ? 'Edit list' : 'New list'}>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <Field label="Name" error={formState.errors.title?.message}>
          <input {...register('title')} className="w-full" autoFocus data-testid="list-form-title" />
        </Field>
        <Field label="Style" hint="Checklist clears ticked items; reusable resets them (packing, routines).">
          <select {...register('style')} className="w-full" data-testid="list-form-style">
            <option value="checklist">Checklist</option>
            <option value="reusable">Reusable</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" {...register('pinned')} /> Pin to the sidebar
        </label>
        {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={onDone}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            disabled={formState.isSubmitting}
            data-testid="list-form-save"
          >
            Save
          </Button>
        </div>
      </form>
    </Sheet>
  )
}

/** Lists attached to a project, task or page, with an add button. */
export const ListsSection = ({
  parentId,
  ws,
  title = 'Lists',
  className = 'mt-6',
  chipOnly,
}: {
  parentId: string
  ws?: string
  title?: string
  className?: string
  /** only the add button, as a chip among others (a task with no lists yet) */
  chipOnly?: boolean
}) => {
  const lists = useListsFor(parentId)
  const [creating, setCreating] = useState(false)
  if (chipOnly)
    return (
      <>
        <Button variant="chip" onClick={() => setCreating(true)} data-testid="list-add">
          Add a list
        </Button>
        {creating && <ListForm parentId={parentId} ws={ws} onDone={() => setCreating(false)} />}
      </>
    )
  return (
    <section className={className} data-testid="lists-section">
      <div className="flex items-center justify-between mb-1.5">
        <h2
          className={className === 'mt-6' ? 'text-[13px] font-semibold text-muted' : 'text-sm font-semibold'}
        >
          {title}
        </h2>
        <Button variant="chip" onClick={() => setCreating(true)} data-testid="list-add">
          New list
        </Button>
      </div>
      {lists.length > 0 && (
        <ul className="group-list">
          {lists.map((l) => (
            <ListRow key={l.id} resource={l} />
          ))}
        </ul>
      )}
      {creating && <ListForm parentId={parentId} ws={ws} onDone={() => setCreating(false)} />}
    </section>
  )
}

/** Lists shown in the workspace scope, pinned first, then most recently touched. */
export const sortLists = (lists: ListResource[]) =>
  [...lists].sort((a, b) =>
    a.spec.pinned !== b.spec.pinned
      ? a.spec.pinned
        ? -1
        : 1
      : a.status.last_touched < b.status.last_touched
        ? 1
        : -1,
  )
